import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type {
  AgingStockQueryDTO,
  DeadStockQueryDTO,
  MarketingRoiQueryDTO,
  ProductPerformanceQueryDTO,
  ProfitByProductQueryDTO,
  ProfitCampaignSummaryQueryDTO,
  SalesFrequencyQueryDTO,
  ShippingPerformanceQueryDTO,
  StockMovementsQueryDTO,
  StockValuationQueryDTO,
  UpdateDeadStockSettingsDTO,
  CustomerReportQueryDTO,
  CustomerTopQueryDTO,
  CustomerGeoDistrictQueryDTO,
  CustomerWatchlistQueryDTO,
  CustomerExportQueryDTO,
  CustomerProductAffinityQueryDTO,
  CustomerCohortsQueryDTO,
} from "@/modules/reports/domain/contracts";
import {
  type ProfitReportQuery,
  type ReportDateRangeQuery,
  reportsApi,
} from "./client";

type QueryEnabled = { enabled?: boolean };

const keys = {
  all: ["reports"] as const,
  summary: (q: ReportDateRangeQuery) => [...keys.all, "summary", q] as const,
  byDay: (q: ReportDateRangeQuery) => [...keys.all, "by-day", q] as const,
  products: (q: ProductPerformanceQueryDTO) =>
    [...keys.all, "products", q] as const,
  agingStock: (q: AgingStockQueryDTO) =>
    [...keys.all, "aging-stock", q] as const,
  deadStock: (q?: DeadStockQueryDTO) => [...keys.all, "dead-stock", q] as const,
  deadStockSettings: () => [...keys.all, "dead-stock-settings"] as const,
  salesFrequency: (q: SalesFrequencyQueryDTO) =>
    [...keys.all, "sales-frequency", q] as const,
  stock: (q: StockMovementsQueryDTO) => [...keys.all, "stock", q] as const,
  profitSummary: (q: ProfitReportQuery) =>
    [...keys.all, "profit-summary", q] as const,
  profitByDay: (q: ProfitReportQuery) =>
    [...keys.all, "profit-by-day", q] as const,
  profitByProduct: (q: ProfitByProductQueryDTO) =>
    [...keys.all, "profit-by-product", q] as const,
  profitCampaigns: (q: ProfitCampaignSummaryQueryDTO) =>
    [...keys.all, "profit-campaigns", q] as const,
  marketingRoi: (q: MarketingRoiQueryDTO) =>
    [...keys.all, "marketing-roi", q] as const,
  shippingPerformance: (q: ShippingPerformanceQueryDTO) =>
    [...keys.all, "shipping-performance", q] as const,
  customerSummary: (q: CustomerReportQueryDTO) =>
    [...keys.all, "customer-summary", q] as const,
  customerTop: (q: CustomerTopQueryDTO) =>
    [...keys.all, "customer-top", q] as const,
  customerGeoProvince: (q: CustomerReportQueryDTO) =>
    [...keys.all, "customer-geo-province", q] as const,
  customerGeoDistrict: (q: CustomerGeoDistrictQueryDTO) =>
    [...keys.all, "customer-geo-district", q] as const,
  customerWatchlist: (q: CustomerWatchlistQueryDTO) =>
    [...keys.all, "customer-watchlist", q] as const,
  customerRiskSummary: (q: CustomerReportQueryDTO) =>
    [...keys.all, "customer-risk-summary", q] as const,
  customerProductAffinity: (q: CustomerProductAffinityQueryDTO) =>
    [...keys.all, "customer-product-affinity", q] as const,
  customerCohorts: (q: CustomerCohortsQueryDTO) =>
    [...keys.all, "customer-cohorts", q] as const,
  stockValuation: (q: StockValuationQueryDTO) =>
    [...keys.all, "stock-valuation", q] as const,
};

export function useSalesSummaryQuery(
  query: ReportDateRangeQuery,
  options?: QueryEnabled,
) {
  return useQuery({
    queryKey: keys.summary(query),
    queryFn: () => reportsApi.salesSummary(query),
    enabled: options?.enabled ?? true,
  });
}

export function useCustomJobsSummaryQuery(
  query: ReportDateRangeQuery,
  options?: QueryEnabled,
) {
  return useQuery({
    queryKey: [...keys.all, "custom-jobs-summary", query] as const,
    queryFn: () => reportsApi.customJobsSummary(query),
    enabled: options?.enabled ?? true,
  });
}

export function useSalesByDayQuery(
  query: ReportDateRangeQuery,
  options?: QueryEnabled,
) {
  return useQuery({
    queryKey: keys.byDay(query),
    queryFn: () => reportsApi.salesByDay(query),
    enabled: options?.enabled ?? true,
  });
}

export function useProductPerformanceQuery(
  query: ProductPerformanceQueryDTO,
  options?: QueryEnabled,
) {
  return useQuery({
    queryKey: keys.products(query),
    queryFn: () => reportsApi.productPerformance(query),
    enabled: options?.enabled ?? true,
  });
}

export function useAgingStockQuery(
  query: AgingStockQueryDTO,
  options?: QueryEnabled,
) {
  return useQuery({
    queryKey: keys.agingStock(query),
    queryFn: () => reportsApi.agingStock(query),
    enabled: options?.enabled ?? true,
  });
}

export function useDeadStockQuery(
  query?: DeadStockQueryDTO,
  options?: QueryEnabled,
) {
  return useQuery({
    queryKey: keys.deadStock(query),
    queryFn: () => reportsApi.deadStock(query),
    enabled: options?.enabled ?? true,
  });
}

export function useDeadStockSettingsQuery(options?: QueryEnabled) {
  return useQuery({
    queryKey: keys.deadStockSettings(),
    queryFn: () => reportsApi.deadStockSettings(),
    enabled: options?.enabled ?? true,
  });
}

export function useUpdateDeadStockSettingsMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateDeadStockSettingsDTO) =>
      reportsApi.updateDeadStockSettings(input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: keys.deadStockSettings() });
      qc.invalidateQueries({ queryKey: [...keys.all, "dead-stock"] });
    },
  });
}

export function useSalesFrequencyQuery(
  query: SalesFrequencyQueryDTO,
  options?: QueryEnabled,
) {
  return useQuery({
    queryKey: keys.salesFrequency(query),
    queryFn: () => reportsApi.salesFrequency(query),
    enabled: options?.enabled ?? true,
  });
}

export function useStockMovementsQuery(
  query: StockMovementsQueryDTO,
  options?: QueryEnabled,
) {
  return useQuery({
    queryKey: keys.stock(query),
    queryFn: () => reportsApi.stockMovements(query),
    enabled: options?.enabled ?? true,
  });
}

export function useProfitSummaryQuery(
  query: ProfitReportQuery,
  options?: QueryEnabled,
) {
  return useQuery({
    queryKey: keys.profitSummary(query),
    queryFn: () => reportsApi.profitSummary(query),
    enabled: options?.enabled ?? true,
  });
}

export function useProfitByDayQuery(
  query: ProfitReportQuery,
  options?: QueryEnabled,
) {
  return useQuery({
    queryKey: keys.profitByDay(query),
    queryFn: () => reportsApi.profitByDay(query),
    enabled: options?.enabled ?? true,
  });
}

export function useProfitByProductQuery(
  query: ProfitByProductQueryDTO,
  options?: QueryEnabled,
) {
  return useQuery({
    queryKey: keys.profitByProduct(query),
    queryFn: () => reportsApi.profitByProduct(query),
    enabled: options?.enabled ?? true,
  });
}

export function useProfitCampaignsQuery(
  query: ProfitCampaignSummaryQueryDTO,
  options?: QueryEnabled,
) {
  return useQuery({
    queryKey: keys.profitCampaigns(query),
    queryFn: () => reportsApi.profitCampaigns(query),
    enabled: options?.enabled ?? true,
  });
}

export function useMarketingRoiQuery(
  query: MarketingRoiQueryDTO,
  options?: QueryEnabled,
) {
  return useQuery({
    queryKey: keys.marketingRoi(query),
    queryFn: () => reportsApi.marketingRoi(query),
    enabled: options?.enabled ?? true,
  });
}

export function useShippingPerformanceQuery(
  query: ShippingPerformanceQueryDTO,
  options?: QueryEnabled,
) {
  return useQuery({
    queryKey: keys.shippingPerformance(query),
    queryFn: () => reportsApi.shippingPerformance(query),
    enabled: options?.enabled ?? true,
  });
}

export function useCustomerReportSummaryQuery(
  query: CustomerReportQueryDTO,
  options?: QueryEnabled,
) {
  return useQuery({
    queryKey: keys.customerSummary(query),
    queryFn: () => reportsApi.customerReportSummary(query),
    enabled: options?.enabled ?? true,
  });
}

export function useCustomerReportTopQuery(
  query: CustomerTopQueryDTO,
  options?: QueryEnabled,
) {
  return useQuery({
    queryKey: keys.customerTop(query),
    queryFn: () => reportsApi.customerReportTop(query),
    enabled: options?.enabled ?? true,
  });
}

export function useCustomerReportByProvinceQuery(
  query: CustomerReportQueryDTO,
  options?: QueryEnabled,
) {
  return useQuery({
    queryKey: keys.customerGeoProvince(query),
    queryFn: () => reportsApi.customerReportByProvince(query),
    enabled: options?.enabled ?? true,
  });
}

export function useCustomerReportByDistrictQuery(
  query: CustomerGeoDistrictQueryDTO,
  options?: QueryEnabled,
) {
  return useQuery({
    queryKey: keys.customerGeoDistrict(query),
    queryFn: () => reportsApi.customerReportByDistrict(query),
    enabled: options?.enabled ?? true,
  });
}

export function useCustomerWatchlistQuery(
  query: CustomerWatchlistQueryDTO,
  options?: QueryEnabled,
) {
  return useQuery({
    queryKey: keys.customerWatchlist(query),
    queryFn: () => reportsApi.customerWatchlist(query),
    enabled: options?.enabled ?? true,
  });
}

export function useCustomerRiskSummaryQuery(
  query: CustomerReportQueryDTO,
  options?: QueryEnabled,
) {
  return useQuery({
    queryKey: keys.customerRiskSummary(query),
    queryFn: () => reportsApi.customerRiskSummary(query),
    enabled: options?.enabled ?? true,
  });
}

export function useCustomerProductAffinityQuery(
  query: CustomerProductAffinityQueryDTO,
  options?: QueryEnabled,
) {
  return useQuery({
    queryKey: keys.customerProductAffinity(query),
    queryFn: () => reportsApi.customerProductAffinity(query),
    enabled: options?.enabled ?? true,
  });
}

export function useCustomerCohortsQuery(
  query: CustomerCohortsQueryDTO,
  options?: QueryEnabled,
) {
  return useQuery({
    queryKey: keys.customerCohorts(query),
    queryFn: () => reportsApi.customerCohorts(query),
    enabled: options?.enabled ?? true,
  });
}

export function useStockValuationQuery(
  query: StockValuationQueryDTO,
  options?: QueryEnabled,
) {
  return useQuery({
    queryKey: keys.stockValuation(query),
    queryFn: () => reportsApi.stockValuation(query),
    enabled: options?.enabled ?? true,
  });
}
